import { ReactNode, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ConfirmOptions {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'default' | 'destructive';
}

interface PromptOptions extends ConfirmOptions {
  inputLabel?: string;
  placeholder?: string;
  defaultValue?: string;
  minLength?: number;
  multiline?: boolean;
}

interface State {
  open: boolean;
  variant: 'confirm' | 'prompt';
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'default' | 'destructive';
  // prompt-only
  inputLabel?: string;
  placeholder?: string;
  defaultValue?: string;
  minLength?: number;
  multiline?: boolean;
  resolve?: (value: boolean | string | null) => void;
}

let openConfirm: ((opts: ConfirmOptions) => Promise<boolean>) | null = null;
let openPrompt: ((opts: PromptOptions) => Promise<string | null>) | null = null;

/**
 * Imperative API used by call sites that want a one-liner
 * replacement for `window.confirm`. Returns a Promise<boolean>.
 *
 *   if (await confirm({ title: 'Delete?' })) { ... }
 *
 * Renders into the singleton `<ConfirmDialogHost />` mounted near the
 * root of the app.
 */
export function confirm(opts: ConfirmOptions): Promise<boolean> {
  if (!openConfirm) {
    // The host isn't mounted (SSR, tests, etc.) — fall back to true
    // rather than blocking the user. Real production renders always
    // have the host mounted in main.tsx.
    return Promise.resolve(true);
  }
  return openConfirm(opts);
}

/**
 * Imperative text-prompt — returns the typed value or null on cancel.
 * Used everywhere we used to call `window.prompt`.
 */
export function promptText(opts: PromptOptions): Promise<string | null> {
  if (!openPrompt) return Promise.resolve(null);
  return openPrompt(opts);
}

export function ConfirmDialogHost() {
  const { t } = useTranslation();
  const [state, setState] = useState<State>({ open: false, title: '', variant: 'confirm' });
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    openConfirm = (opts) => new Promise<boolean>((resolve) => {
      setState({ open: true, variant: 'confirm', resolve: resolve as (v: boolean | string | null) => void, ...opts });
    });
    openPrompt = (opts) => new Promise<string | null>((resolve) => {
      setInputValue(opts.defaultValue ?? '');
      setState({ open: true, variant: 'prompt', resolve: resolve as (v: boolean | string | null) => void, ...opts });
    });
    return () => { openConfirm = null; openPrompt = null; };
  }, []);

  const close = (value: boolean | string | null) => {
    state.resolve?.(value);
    setState((s) => ({ ...s, open: false }));
  };

  const submitPrompt = () => {
    const v = inputValue.trim();
    if (state.minLength != null && v.length < state.minLength) return;
    if (!v) { close(null); return; }
    close(v);
  };

  return (
    <AnimatePresence>
      {state.open && (
        <motion.div
          key="backdrop"
          className="fixed inset-0 z-[100] bg-foreground/45 backdrop-blur-sm grid place-items-center px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onClick={() => close(state.variant === 'prompt' ? null : false)}
        >
          <motion.div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-title"
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.18, ease: [0.16, 0.84, 0.44, 1] as const }}
            className="relative w-full max-w-md rounded-2xl bg-white dark:bg-card shadow-glow border border-border p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <span className={`grid h-10 w-10 place-items-center rounded-xl shrink-0 ${
                state.tone === 'destructive'
                  ? 'bg-destructive/10 text-destructive'
                  : 'bg-primary/10 text-primary'
              }`}>
                {state.variant === 'prompt' ? <MessageSquare className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
              </span>
              <div className="flex-1 min-w-0">
                <h2 id="confirm-title" className="text-lg font-semibold tracking-tight">{state.title}</h2>
                {state.description && (
                  <p className="mt-1.5 text-sm text-muted-foreground text-pretty">{state.description}</p>
                )}
              </div>
            </div>

            {state.variant === 'prompt' && (
              <div className="mt-4">
                {state.inputLabel && (
                  <label className="text-xs font-semibold text-muted-foreground">{state.inputLabel}</label>
                )}
                {state.multiline ? (
                  <textarea
                    autoFocus
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder={state.placeholder}
                    rows={3}
                    className="mt-1 w-full rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-y"
                  />
                ) : (
                  <input
                    autoFocus
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder={state.placeholder}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitPrompt(); } }}
                    className="mt-1 w-full h-10 rounded-xl border border-border bg-card px-3 text-sm outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                  />
                )}
                {state.minLength != null && (
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    {t('common.minChars', { n: state.minLength })}
                  </p>
                )}
              </div>
            )}

            <div className="mt-6 flex gap-2 justify-end">
              <button
                onClick={() => close(state.variant === 'prompt' ? null : false)}
                className="inline-flex h-10 items-center px-4 rounded-xl border border-border text-sm font-semibold hover:bg-muted transition"
              >
                {state.cancelLabel ?? t('common.cancel')}
              </button>
              <button
                onClick={() => state.variant === 'prompt' ? submitPrompt() : close(true)}
                autoFocus={state.variant === 'confirm'}
                disabled={
                  state.variant === 'prompt' &&
                  state.minLength != null &&
                  inputValue.trim().length < state.minLength
                }
                className={`inline-flex h-10 items-center px-4 rounded-xl text-sm font-semibold text-white transition shadow-glow disabled:opacity-50 disabled:pointer-events-none ${
                  state.tone === 'destructive'
                    ? 'bg-gradient-to-r from-rose-600 to-red-600 hover:shadow-glow-pink'
                    : 'gradient-primary hover:shadow-glow-pink'
                }`}
              >
                {state.confirmLabel ?? t('common.confirm')}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * Convenience wrapper for inline JSX use: pass children that should
 * pop the confirm dialog on click, with the options.
 */
export function ConfirmTrigger({
  options,
  onConfirmed,
  children,
}: {
  options: ConfirmOptions;
  onConfirmed: () => void;
  children: (open: () => void) => ReactNode;
}) {
  const open = async () => {
    if (await confirm(options)) onConfirmed();
  };
  return <>{children(open)}</>;
}
